import React, { useCallback, useState } from 'react';
import { Button, InlineNotification } from '@carbon/react';
import { CrudBulkAction } from './defineResource';
import { useCrudConfig } from './provider';

export interface CrudBatchBarProps<TEntity> {
  selectedRows: TEntity[];
  actions: CrudBulkAction<TEntity>[];
  onCancel: () => void;
  /** True while an action is in flight; the bar stays up but refuses new work. */
  running: boolean;
  /**
   * Asks before an `isDelete` action runs, as the single-row delete does. Called with
   * `proceed`, which runs the action exactly as a click would — a failure still lands in
   * this bar. The dialog is the caller's to render: this bar sits in the toolbar's own
   * stacking context, which would put a modal under the shell's header and rail.
   * Omit it and a delete runs on click.
   */
  confirmDelete?: (proceed: () => Promise<void>) => void;
}

/**
 * Replaces the toolbar's contents while a selection exists, rather than adding
 * controls beside them — the row is a fixed 3rem and already full at 375px.
 */
export function CrudBatchBar<TEntity>(props: CrudBatchBarProps<TEntity>) {
  const { translate, resolveErrorMessage } = useCrudConfig();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const count = props.selectedRows.length;
  const offered = props.actions.filter((action) => !action.enabledFor || action.enabledFor(props.selectedRows));

  /**
   * Task 4's `runBulk` wraps `action.run` in try/finally with no catch, so a rejection
   * propagates straight out to whoever called `run` — here, still attached to React. Left
   * unhandled it is an unhandled promise rejection AND the user sees nothing after a failed
   * bulk action. Caught here, resolved through the same `resolveErrorMessage` convention as
   * CrudFormModal/CrudDeleteModal, and rendered inline. Cleared at the start of every run so
   * a stale failure from a previous action does not linger next to a fresh one.
   */
  const handleRun = useCallback(
    async (action: CrudBulkAction<TEntity>) => {
      setErrorMessage(null);
      try {
        await action.run(props.selectedRows);
      } catch (error) {
        setErrorMessage(resolveErrorMessage(error));
      }
    },
    [props.selectedRows, resolveErrorMessage]
  );

  return (
    <div className="crud-batch-bar__wrap">
      <div className="crud-batch-bar">
        <span className="crud-batch-bar__count">{translate('common.selectedCount', { count })}</span>
        <div className="crud-batch-bar__actions">
          {offered.map((action) => (
            <Button
              key={action.labelKey}
              size="lg"
              // The danger kind is kept for its screen-reader description (below); its red is
              // not — `crud-batch-bar__action` repaints every button on-colour for the blue bar.
              kind={action.isDelete ? 'danger--ghost' : 'ghost'}
              className="crud-batch-bar__action"
              // Carbon's danger variants inject a visually-hidden, non-empty "danger" span
              // (via aria-describedby) that ALSO folds into the accessible NAME, turning
              // "Delete" into "dangerDelete". An explicit aria-label short-circuits name
              // computation to exactly the label, while dangerDescription is left at its
              // real default so the danger cue still reaches screen readers as a description.
              aria-label={translate(action.labelKey)}
              disabled={props.running}
              onClick={() =>
                action.isDelete && props.confirmDelete
                  ? props.confirmDelete(() => handleRun(action))
                  : handleRun(action)
              }
            >
              {translate(action.labelKey)}
            </Button>
          ))}
          <Button size="lg" kind="ghost" className="crud-batch-bar__action" onClick={props.onCancel}>
            {translate('actions.cancel')}
          </Button>
        </div>
      </div>
      {errorMessage && (
        <InlineNotification kind={'error'} lowContrast hideCloseButton title={''} subtitle={errorMessage} />
      )}
    </div>
  );
}
